import { DatePipe, DecimalPipe } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    OnInit,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import {
    DocumentController,
    DocumentItemPartial,
    FavoriteFieldEntity,
} from '@domain';
import { environment } from '@environments';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaDialogConfirmationService } from '@shared/components';
import { forkJoin, of, switchMap } from 'rxjs';
import { DocumentFieldsController } from 'src/app/domain/controllers/document/document-fields.controller';
import { TrashDocumentItemEntity } from 'src/app/domain/entities/trash-document-item.entity';
import { RaEditImageComponent } from 'src/app/shared/components/ra-edit-image/ra-edit-image.component';
import { RaImageFullScreenModel } from 'src/app/shared/components/ra-images-full-screen/model/ra-image-full-screen.model';
import { RaImagesFullScreenComponent } from 'src/app/shared/components/ra-images-full-screen/ra-images-full-screen.component';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { DigitalOceanStorage } from 'src/app/shared/utils/digital-ocean/digital-ocean-storage';
import { deltaToPlainText } from 'src/app/shared/utils/delta.util';
import { EditedImageResult } from 'src/app/shared/components/ra-edit-image/model/edited-image-result.model';
import { MediaUtils } from 'src/app/shared/utils/media.utils';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { EditService } from '../../../services/edit-service';
import { ModalImageDateComponent } from '../../modals/modal-image-date/modal-image-date.component';
import { CardBaseComponent } from '../_card-base/card-base.component';
import { CardDescriptionAreaComponent } from '../_card-base/card-description-area.component';
import { CardTypes } from '../_card-base/enums/card-types.enum';
import { DynamicActionModel } from '../_card-base/model/dynamic-action.model';
import { imageNumberForItem } from '../../../utils/image-number.util';
import { buildSectionGallery } from '../../../utils/section-gallery.util';

import { MediaLookChipComponent } from '../_card-base/media-look-chip/media-look-chip.component';
import {
    hasAnyKey,
    withCaptionStyle,
    withMediaFill,
    withOverride,
    withoutKeys,
} from '../_card-base/item-look.util';
import { MEDIA_FILL_DEFAULT, resolveMediaFill } from '../../../../../media-fill-options';
import { CaptionStyle, resolveCaptionStyle } from '../../../../../pdf-renderer/caption-style';

@Component({
    selector: 'group-image-card',
    templateUrl: 'group-image-card.component.html',
    styleUrl: 'group-image-card.component.scss',
    imports: [
        MediaLookChipComponent,
        CardBaseComponent,
        CardDescriptionAreaComponent,
        DatePipe,
        DecimalPipe,
        ModalImageDateComponent,
        RaImagesFullScreenComponent,
        RaEditImageComponent,
        RaSquareIconComponent,
        TranslatePipe,
    ],
})
export class GroupImageCardComponent implements OnInit {
    public field = input<DocumentItemPartial>();
    public fontFamily = input<string>(environment.DEFAULT_FONT_FAMILY);
    public favoriteField = input<FavoriteFieldEntity>();
    public trashField = input<TrashDocumentItemEntity>();
    public type = input<CardTypes>(CardTypes.DEFAULT);
    public selected = input<boolean>(false);
    public imageActions: DynamicActionModel[] = [];
    public showFullScreen = signal(false);
    public fullScreenIndex = signal(0);
    /** Galeria montada ao abrir a tela cheia (todas as imagens da seção). */
    public galleryImages = signal<RaImageFullScreenModel[]>([]);
    public showEditImage = signal(false);
    public showEditImagePosition = signal<number | null>(null);
    /** Painel "Foto"/"Grade"/"Vídeo" do rail: é ele que a prévia abre. */
    private _lookChip = viewChild(MediaLookChipComponent);
    /** Índice da foto com o modal de data aberto; null = fechado. */
    public dateModalIndex = signal<number | null>(null);

    // Número/data por foto, espelhando preview e PDF: config por-item tem
    // precedência sobre a config do documento (mesma regra do image-data).
    public showImageNumber = computed<boolean>(
        () =>
            this.field()?.configs?.showImageNumber ??
            this.editDataService.document()?.config?.showImageNumber ??
            false
    );
    public showImageDate = computed<boolean>(
        () =>
            this.field()?.configs?.showImageDate ??
            this.editDataService.document()?.config?.showImageDate ??
            false
    );
    /** Nº da 1ª foto do grupo no documento (foto `i` = este valor + i). */
    public firstImageNumber = computed<number | null>(() =>
        imageNumberForItem(this.editDataService.document(), this.field()?._id)
    );
    public cardTypes = CardTypes;

    /**
     * Posições do GRUPO entregues ao editor, na ordem em que ele as recebeu.
     *
     * O `position` que o editor emite é o índice DENTRO da lista que ele recebeu,
     * não a posição no grupo. Como o card abre o editor com uma imagem só
     * (`[imageUrl]`), esse índice é sempre 0 — usá-lo direto gravava toda edição
     * na 1ª imagem do grupo (editar a 2ª sobrescrevia a 1ª). Esta lista faz a
     * tradução e continua correta se um dia o editor for aberto com o grupo todo.
     */
    private _editorGroupPositions: number[] = [];

    // * [Injects]
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    public editItemsService = inject(EditItemsService);
    private _mediaUtils = inject(MediaUtils);
    private _documentController = inject(DocumentController);
    private _digitalOceanStorage = inject(DigitalOceanStorage);
    private _documentFieldsController = inject(DocumentFieldsController);
    private _raDialogConfirmationService = inject(RaDialogConfirmationService);
    private _translate = inject(TranslateService);

    constructor() {
        /*
         * A prévia pede as configurações deste item pelo menu que ela mostra em cima dele.
         * Abre o MESMO painel do botão do rail (cada card tem o seu), então o pedido chega
         * por um signal e o card dono abre o painel dele, e limpa o pedido, senão voltar
         * para a seção mais tarde reabriria o painel do nada.
         *
         * O painel só existe depois que a view do card monta: até lá o pedido espera, e o
         * effect roda de novo quando o `viewChild` resolve.
         */
        effect(() => {
            const requested = this.editDataService.itemConfigRequestId();
            if (!requested || requested !== this.field()?._id) return;

            const lookChip = this._lookChip();
            if (!lookChip) return;

            untracked(() => {
                lookChip.open();
                this.editDataService.clearItemConfigRequest();
            });
        });
    }

    ngOnInit(): void {
        this.buildDynamicActions();
    }

    private buildDynamicActions(): void {
        const tr = this._translate;
        this.imageActions = [
            // As configurações (fotos por linha, enquadramento, número e data) são a paleta
            // do rail; as ações do grupo ficam no menu ⋯.
            {
                id: 'add',
                icon: 'add',
                tooltip: tr.instant('document.edit.groupImageCard.addMoreImages'),
                action: () =>
                    this.editItemsService.addImageToField(this.getCurrentField()._id).subscribe(),
            },
            // "Tela cheia" saiu do menu: clicar em qualquer foto abre a galeria.
        ];
    }

    /** Chaves do menu "Grade" que o card pode sobrescrever. */
    private static readonly LOOK_KEYS = [
        'groupImageGrid',
        'fullImage',
        'showImageNumber',
        'showImageDate',
        'captionStyle',
        'imageFillColor',
    ] as const;

    /** Cor do fundo ao vivo, enquanto o seletor livre do painel é arrastado. */
    private _liveFill = signal<string | null>(null);

    /** Cor do fundo efetiva (card, documento), como no PDF; a ao vivo por cima. */
    public fillColor = computed(
        () =>
            this._liveFill() ??
            resolveMediaFill([
                this.getCurrentField()?.configs,
                this.editDataService.document()?.config,
            ])
    );

    /**
     * A var `--media-fill` do card: só com cor escolhida. Sem ela a sobra fica no cinza do
     * tema (--backgroundSecondary), que no tema claro é o mesmo cinza padrão do PDF.
     */
    public fillStyle = computed(() => {
        const color = this.fillColor();
        return color === MEDIA_FILL_DEFAULT ? null : color;
    });

    /** Cor ao vivo vinda do seletor livre do painel (null = volta à salva). */
    public previewFill(color: string | null): void {
        this._liveFill.set(color);
    }

    /** Cor do fundo pelo painel (igual à do documento vira herança). */
    public setFill(color: string): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withMediaFill(field.configs, color, this.editDataService.document()?.config)
        );
    }

    /** Estilo da legenda efetivo (card, documento), como no PDF. */
    public captionStyle = computed<CaptionStyle>(() =>
        resolveCaptionStyle([
            this.getCurrentField()?.configs,
            this.editDataService.document()?.config,
        ])
    );

    /** Estilo da legenda pelo menu "Grade" (igual ao documento vira herança). */
    public setCaptionStyle(style: CaptionStyle): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withCaptionStyle(field.configs, style, this.editDataService.document()?.config)
        );
    }

    /** "Imagem inteira" efetivo: override do card, senão a config do documento. */
    public isFullImage = computed<boolean>(
        () =>
            this.getCurrentField()?.configs?.fullImage ??
            this.editDataService.document()?.config?.fullImage ??
            false
    );

    /** Algo do menu "Grade" está sobrescrito neste card (mostra "Voltar ao padrão"). */
    public lookOverridden = computed(() =>
        hasAnyKey(this.getCurrentField()?.configs, GroupImageCardComponent.LOOK_KEYS)
    );

    /** Fotos por linha deste grupo (igual ao documento vira herança). */
    public setColumns(columns: number): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        const inherited = this.editDataService.document()?.config?.groupImageGrid ?? 2;
        this.editItemsService.updateItemConfig(
            field._id,
            withOverride(field.configs, 'groupImageGrid', columns, inherited)
        );
    }

    /** Enquadramento, número e data pelo menu "Grade" (igual ao documento vira herança). */
    public setLook(key: 'fullImage' | 'showImageNumber' | 'showImageDate', value: boolean): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        const inherited = this.editDataService.document()?.config?.[key] ?? false;
        this.editItemsService.updateItemConfig(
            field._id,
            withOverride(field.configs, key, value, inherited)
        );
    }

    /** "Voltar ao padrão do documento": só as chaves da grade; o estilo do texto fica. */
    public restoreLook(): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withoutKeys(field.configs, GroupImageCardComponent.LOOK_KEYS)
        );
    }

    /**
     * Salva as configurações só deste grupo (auto-save do modal).
     * @param configs Override do item — o que não estiver aqui segue o documento
     */
    /** Data atual da foto `index` do grupo (entries são maps com url/date). */
    public groupEntryDate(index: number): Date | undefined {
        const value = this.getCurrentField()?.value;
        if (!Array.isArray(value)) return undefined;
        const raw = (value[index] as { date?: string | Date } | undefined)?.date;
        if (!raw) return undefined;
        const date = new Date(raw);
        return isNaN(date.getTime()) ? undefined : date;
    }

    /** Confirmação do modal de data: aplica otimista e persiste (com mediaId quando houver). */
    public onSaveImageDate(date: Date): void {
        const index = this.dateModalIndex();
        this.dateModalIndex.set(null);
        if (index === null) return;

        const field = this.getCurrentField();
        const entry = Array.isArray(field?.value)
            ? (field.value[index] as { _id?: string } | undefined)
            : undefined;

        this.editItemsService.changeGroupImageDate(field._id, index, date, entry?._id);
    }

    /**
     * Abre a tela cheia posicionada na imagem clicada — no editor, com TODAS
     * as imagens da seção (navegação igual ao preview); fora dele
     * (lixeira/favoritos), só com as mídias deste grupo.
     * @param index Índice da imagem dentro do grupo
     */
    public openFullScreen(index: number): void {
        if (this.type() === CardTypes.DEFAULT) {
            const { images, startIndex } = buildSectionGallery(
                this.editDataService.currentSession()?.items ?? [],
                this.getCurrentField()._id,
                index,
                this.getDescriptionText()
            );

            if (images.length > 0) {
                this.galleryImages.set(images);
                this.fullScreenIndex.set(startIndex);
                this.showFullScreen.set(true);
                return;
            }
        }

        this.galleryImages.set(this.getFullScreenImage());
        this.fullScreenIndex.set(index);
        this.showFullScreen.set(true);
    }

    public getFullScreenImage(): RaImageFullScreenModel[] {
        const field = this.getCurrentField();
        return field.value.map(image => ({
            url: image.url,
            alt: this.getDescriptionText(),
            title: this.getDescriptionText(),
        }));
    }

    /**
     * Retorna o texto simples da descrição (extrai do formato Delta se necessário).
     * A description vem como Quill Delta; sem achatar, o alt/title exibiria o JSON cru
     * ("insert", aspas, chaves).
     */
    public getDescriptionText(): string {
        const field = this.getCurrentField();
        const plainText = deltaToPlainText(field.description);

        return plainText || this._translate.instant('document.edit.groupImageCard.defaultAlt');
    }

    /**
     * Retorna a lista de URLs das imagens para edição
     */
    public getImageUrlByPosition(): string {
        const field = this.getCurrentField();
        return field.value[this.showEditImagePosition()].url;
    }

    /**
     * Manipula o evento quando as imagens são salvas
     * @param editedImages Lista de imagens editadas com position, file e size
     */
    public onImagesSaved(editedImages: EditedImageResult[]): void {
        // Fecha o modal
        this.showEditImage.set(false);

        // Se não há imagens editadas, não faz nada
        if (!editedImages || editedImages.length === 0) return;

        // Overlay bloqueante: o upload leva segundos e sem ele a tela parecia travada
        this.editService.fieldMediaActionKey.set('document.edit.groupImageCard.savingEditedImage');

        // 1- Solicita as URLs de upload para o documento
        const uploadItems = editedImages.map(img => ({
            mimeType: img.file.type,
            size: img.size,
        }));

        this._documentController
            .generateUrlsToUploads(this.editDataService.getDocumentId(), uploadItems)
            .pipe(
                // 2- Realiza o upload de todas as imagens editadas
                switchMap((urls: string[]) => {
                    const uploadObservables = editedImages.map((img, index) =>
                        this._digitalOceanStorage.uploadToSignedUrl(img.file, urls[index])
                    );
                    return forkJoin(uploadObservables);
                }),
                // 3- Atualiza as imagens no backend usando changeMediaFromGroupField
                switchMap((newUrls: string[]) => {
                    // O `position` do editor é o índice DENTRO da lista que ele
                    // recebeu — com uma imagem só, sempre 0. Traduzimos para a
                    // posição real no grupo (ver _editorGroupPositions) e mandamos
                    // também o `mediaId`, que é imune a posição defasada.
                    const items = editedImages.map((img, index) => {
                        const groupPosition =
                            this._editorGroupPositions[img.position] ??
                            this.showEditImagePosition() ??
                            0;

                        return {
                            position: groupPosition,
                            mediaId: this.getMediaId(groupPosition),
                            newImageUrl: newUrls[index],
                            newImageSize: img.size,
                        };
                    });

                    const field = this.getCurrentField();
                    return this._documentFieldsController.changeMediaFromGroupField(
                        this.editDataService.getDocumentId(),
                        this.editDataService.getCurrentSessionId(),
                        field._id,
                        items
                    );
                })
            )
            .subscribe({
                next: () => this.editService.fieldMediaActionKey.set(null),
                error: () => this.editService.fieldMediaActionKey.set(null),
            });
    }

    public removeIndividualImage(position: number): void {
        this._raDialogConfirmationService
            .confirm({
                title: this._translate.instant('document.edit.groupImageCard.removeImageTitle'),
                message: this._translate.instant('document.edit.groupImageCard.removeImageMessage'),
                confirmText: this._translate.instant(
                    'document.edit.groupImageCard.removeImageConfirm'
                ),
                cancelText: this._translate.instant('common.cancel'),
                type: 'danger',
                irreversible: true,
            })
            .subscribe({
                next: dialogControl => {
                    dialogControl.setLoading(true);

                    const field = this.getCurrentField();
                    const mediaId = this.getMediaId(position);
                    this._documentFieldsController
                        .removeImageFromGroupField(
                            this.editDataService.getDocumentId(),
                            this.editDataService.getCurrentSessionId(),
                            field._id,
                            [position],
                            mediaId ? [mediaId] : undefined
                        )
                        .subscribe({
                            next: () => dialogControl.close(),
                            error: () => dialogControl.setLoading(false),
                        });
                },
                error: () => {},
            });
    }

    public changeIndividualImage(position: number): void {
        const items: {
            index: number;
            file: File;
            mimeType: string;
            size: number;
            url?: string;
        }[] = [];

        // Overlay bloqueante — ver onImagesSaved
        this.editService.fieldMediaActionKey.set('document.edit.groupImageCard.swappingImage');

        // 1- Busca as imagens da galeria do PC, e comprimi as imagens
        this._mediaUtils
            .selectImagesFromGallery()
            .pipe(
                // 2- Envia os dados para o backend gerar as URLs do upload
                switchMap(images => {
                    if (images.length === 0) {
                        // Usuário cancelou a operação, não continua o fluxo
                        return of(null);
                    }

                    images.forEach((image, index) => {
                        items.push({
                            index: index,
                            file: image,
                            mimeType: image.type,
                            size: this._mediaUtils.getFileSizeInGB(image),
                        });
                    });

                    return this._documentController.generateUrlsToUploads(
                        this.editDataService.getDocumentId(),
                        items
                    );
                }),
                // 3- Envia para realizar o upload das mídias
                switchMap(urls => {
                    return this._digitalOceanStorage.uploadToSignedUrl(items[0].file, urls[0]);
                }),
                // 4- Altera a imagem no backend
                switchMap((newUrl: string) => {
                    const field = this.getCurrentField();
                    return this._documentFieldsController.changeMediaFromGroupField(
                        this.editDataService.getDocumentId(),
                        this.editDataService.getCurrentSessionId(),
                        field._id,
                        [
                            {
                                position: position,
                                mediaId: this.getMediaId(position),
                                newImageUrl: newUrl,
                                newImageSize: this._mediaUtils.getFileSizeInGB(items[0].file),
                            },
                        ]
                    );
                })
            )
            .subscribe({
                next: () => this.editService.fieldMediaActionKey.set(null),
                error: () => this.editService.fieldMediaActionKey.set(null),
            });
    }

    public editIndividualImage(position: number): void {
        this.showEditImagePosition.set(position);
        // O editor recebe SÓ esta imagem (`[imageUrl]`), então o índice 0 dele
        // corresponde a esta posição do grupo (ver _editorGroupPositions).
        this._editorGroupPositions = [position];
        this.showEditImage.set(true);
    }

    /** `_id` da mídia numa posição do grupo (ausente em grupos ainda sem backfill). */
    private getMediaId(position: number): string | undefined {
        const media = this.getCurrentField().value?.[position];
        return media?._id;
    }

    /**
     * Retorna o campo atual
     * @returns DocumentItemPartial | TrashDocumentItemEntity | FavoriteFieldEntity
     */
    public getCurrentField(): DocumentItemPartial {
        switch (this.type()) {
            case 'default':
                return this.field();
            case 'trash':
                return this.trashField().item;
            case 'favorite':
                return this.favoriteField().field;
        }
    }
}
